import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "components";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import uniqueId from "lodash-es/uniqueId";
import styles from "./FileUploader.module.css";
import FilesList from "./components";
import { utils, operations, types, constants } from "./duck";
import InputError from "../../../input-error";
import { formHooks } from "../../../../form/duck";

const areaDescribedByPrefix = "file-info";
const areaDescribedByNamePrefix = "file-name";

const FileUploader: React.FC<types.FileUploaderProps> = ({
  containerClassName,
  name,
  label,
  labelClassName,
  uploadHint,
  uploadHintClassName,
  allowedFilesExtensions = constants.DEFAULT_FILES_EXTENSIONS,
  maxFileSize = 100,
  maxElements = 5,
  endpoint,
  renderListItemCallback,
  fileForUploading,
  classNameFilesList,
}) => {
  const [validationMessage, setValidationMessage] = useState("");
  const [loadingFiles, setLoadingFiles] = useState<types.loadingFile[]>([]);
  const { t } = useTranslation();
  const id = useMemo(() => `file-${name}`, [name]);
  const errorId = useMemo(() => uniqueId("error-"), []);
  const [{ value = [] }, { error }, { setValue }] = formHooks.useField<
    object[]
  >({
    name,
  });

  const isLimitNotReached = useMemo(
    () => loadingFiles.length < maxElements,
    [loadingFiles.length, maxElements],
  );

  const handleUploadFiles: types.handleUploadFiles = ({ files, input }) => {
    if (files && files.length && isLimitNotReached) {
      const validationResult = operations.validateFiles({
        files,
        allowedFilesExtensions,
        maxFileSize,
        maxElements,
        filesCount: [...loadingFiles, ...value].length,
      });

      if (validationResult) {
        setValidationMessage(validationResult);
        return;
      }

      if (validationMessage) {
        setValidationMessage("");
      }

      operations
        .uploadFiles({ files, setLoadingFiles, endpoint })
        .catch(() => {});

      if (input) {
        input.value = "";
      }
    }
  };

  useEffect(() => {
    if (fileForUploading) {
      handleUploadFiles({ files: [fileForUploading] });
    }
  }, [fileForUploading]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { files } = e.target;
    handleUploadFiles({ files, input: e.target });
  };

  const inputAreaDescribedByAttrs = useMemo(
    () =>
      loadingFiles.map((item) => {
        const fileStatusPart = `${
          item.errorMessage || item.reference
            ? ` ${areaDescribedByPrefix}-${item.tempId}`
            : ""
        }`;

        return `${areaDescribedByNamePrefix}-${item.tempId}${fileStatusPart}`;
      }),
    [loadingFiles],
  );

  return (
    <>
      <div className={classNames(styles.fileUploader, containerClassName)}>
        <input
          multiple
          type="file"
          accept={utils.mapExtensions(allowedFilesExtensions)}
          className={styles.input}
          id={id}
          name={name}
          onChange={handleChange}
          aria-describedby={inputAreaDescribedByAttrs.join(" ")}
          aria-errormessage={errorId}
          aria-invalid={!!error}
        />
        <label
          htmlFor={id}
          className={classNames({
            [styles.labelDisabled]: !isLimitNotReached,
          })}
        >
          {label || (
            <span className={classNames(styles.label, labelClassName)}>
              <Icon name="upload-outlined" className={styles.uploadIcon} />
              {t("fileUploader.label")}
            </span>
          )}
        </label>
        <div className={classNames(styles.uploadHint, uploadHintClassName)}>
          {uploadHint}
        </div>
        <InputError error={validationMessage || error} errorId={errorId} />
      </div>
      <FilesList
        areaDescribedByNamePrefix={areaDescribedByNamePrefix}
        areaDescribedByPrefix={areaDescribedByPrefix}
        renderListItemCallback={renderListItemCallback}
        loadingFiles={loadingFiles}
        setFilesCallback={setLoadingFiles}
        setValueCallback={setValue}
        value={value}
        classNameContainer={classNameFilesList}
      />
    </>
  );
};

export default FileUploader;
